// src/services/api.ts
import axios from 'axios';
import { ApiResponse, RestaurantMenuData } from '../types';
import { cacheService } from './cacheService';

const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:5000/api';
class MenuService {
  private pendingRequests: Map<string, Promise<ApiResponse>> = new Map();
  private offlineListeners: Array<(isOnline: boolean) => void> = [];

  constructor() {
    // Listen to online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  private handleOnline() {
    this.notifyOfflineListeners(true);
  }

  private handleOffline() {
    this.notifyOfflineListeners(false);
  }

  private notifyOfflineListeners(isOnline: boolean) {
    this.offlineListeners.forEach(listener => listener(isOnline));
  }

  onOfflineStatusChange(listener: (isOnline: boolean) => void) {
    this.offlineListeners.push(listener);
    return () => {
      const index = this.offlineListeners.indexOf(listener);
      if (index > -1) this.offlineListeners.splice(index, 1);
    };
  }

  async isOnline(): Promise<boolean> {
  // Check navigator.onLine first
  if (!navigator.onLine) return false;
  
  // Try to make a lightweight request to verify actual connectivity
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    await fetch('https://www.google.com/favicon.ico', {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return true;
  } catch (error) {
    return false;
  }
}

  async fetchMenu(restaurantId: string, skipCache = false): Promise<ApiResponse> {
    const cacheKey = `menu_${restaurantId}`;
    
    // Prevent duplicate requests
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    const requestPromise = this.executeFetch(restaurantId, cacheKey, skipCache);
    this.pendingRequests.set(cacheKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async executeFetch(restaurantId: string, cacheKey: string, skipCache: boolean): Promise<ApiResponse> {
    // Check cache first
    if (!skipCache) {
      const cachedData = await cacheService.get(cacheKey);
      if (cachedData) {
        
        // If online, refresh cache in background
        if (navigator.onLine) {
          this.refreshCacheInBackground(restaurantId, cacheKey);
        }
        
        return { success: true, data: cachedData };
      }
    }

    // If offline and no cache, return error
    if (!navigator.onLine) {
      return {
        success: false,
        data: null as any,
        error: 'You are offline. Please check your internet connection.',
      };
    }

    try {
      
      const response = await axios.get(`${API_BASE_URL}/menu/public/${restaurantId}`, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000, // 10 second timeout
      });
      
      const data = response.data;
      
      if (data.success && data.data) {
        // Cache the response
        await cacheService.set(cacheKey, data.data);
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching menu:', error);
      
      // Try to return stale cache if available
      const staleCache = await cacheService.get(cacheKey);
      if (staleCache) {
        return {
          success: true,
          data: staleCache,
          error: 'Using cached data due to network issues.',
        };
      }
      
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Failed to fetch menu',
      };
    }
  }

  private async refreshCacheInBackground(restaurantId: string, cacheKey: string) {
    try {
      const response = await axios.get(`${API_BASE_URL}/menu/public/${restaurantId}`, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 5000,
      });
      
      const data = response.data;
      if (data.success && data.data) {
        await cacheService.set(cacheKey, data.data);
      }
    } catch (error) {
      console.log('Background cache refresh failed:', error);
    }
  }

  async refreshMenu(restaurantId: string): Promise<ApiResponse> {
    const cacheKey = `menu_${restaurantId}`;
    await cacheService.delete(cacheKey);
    return this.fetchMenu(restaurantId, true);
  }

  async clearCache(): Promise<void> {
    await cacheService.clear();
  }

  async getCacheStats() {
    return cacheService.getStats();
  }
}

export const menuService = new MenuService();

// Export convenience methods
export const fetchMenu = (restaurantId: string, skipCache?: boolean) => 
  menuService.fetchMenu(restaurantId, skipCache);
export const refreshMenu = (restaurantId: string) => menuService.refreshMenu(restaurantId);
export const clearCache = () => menuService.clearCache();